import cookies from './cookies';
import SessionStorageManager from './session-storage-manager';
import { v4 as uuidv4 } from 'uuid';
import http from './http';
import { getConfig } from '../ImportantMessageConfigs/config-registry';
import { messageService } from './message-service';
import { buildUrlWithoutNullishParams } from '@qualtrics/page-http-client/lib/getQueryString';

const cache = new SessionStorageManager(window);

const messageIdCacheKey = (sessionId) => `GW_imba_id_${sessionId}`;
const messagesStateCacheKey = (sessionId) => `GW_imba_state_${sessionId}`;
const messagesRefreshAtCacheKey = (sessionId) =>
  `GW_imba_refresh_at_${sessionId}`;
const messageServiceEndpoint = '/wrapper/v1/messages';

const buildMessageState = () => ({
  isMessagesRead: false,
});

export const initializeIMBA = async () => {
  const sessionId = cookies.get('UDSSessionKey');

  let messageServiceUrl = buildUrlWithoutNullishParams(messageServiceEndpoint, {
    UDSSessionKey: sessionId,
  });

  const messagesData = await getMessageData(sessionId, messageServiceUrl);
  const hasPartialError = !!messagesData.errors.length;

  return {
    messagesToDisplay: getMessagesToDisplay(messagesData),
    hasPartialError,
  };
};

export const clearIMBACache = () => {
  const sessionId = cookies.get('UDSSessionKey');
  cache.removeSessionStorage(messageIdCacheKey(sessionId));
  cache.removeSessionStorage(messagesStateCacheKey(sessionId));
  cache.removeSessionStorage(messagesRefreshAtCacheKey(sessionId));
};

export const getCacheMessagesState = async () => {
  const sessionId = cookies.get('UDSSessionKey');
  let cacheKey, messagesState;

  if (sessionId) {
    cacheKey = messagesStateCacheKey(sessionId);
    messagesState = cache.getSessionStorage(cacheKey);
  }

  if (messagesState) {
    messagesState = await JSON.parse(messagesState);
  } else {
    messagesState = buildMessageState();
    if (sessionId)
      cache.setSessionStorage(cacheKey, JSON.stringify(messagesState));
  }
  return messagesState;
};

export const cacheNewMessageState = async (newMessageStateProps) => {
  const sessionId = cookies.get('UDSSessionKey');
  const currentMessageState = await getCacheMessagesState();
  const newMessageState = { ...currentMessageState, ...newMessageStateProps };
  cache.setSessionStorage(
    messagesStateCacheKey(sessionId),
    JSON.stringify(newMessageState),
  );
  return newMessageState;
};

/**
 * @typedef {Object} messageError
 * @property {string} messageId
 * @property {string} error
 */

/**
 * @typedef {Object} message
 * @property {string} messageId
 * @property {Object} [additionalAttributes]
 */

/**
 * @typedef {Object} GWSMessages
 * @property {Array.<messageError>} errors
 * @property {Array.<message>} messages
 */

/**
 * It returns data retrieved form GWS's /wrapper/messages
 * @param {string} sessionId
 * @param {string} serviceUrl
 * @returns {Promise<GWSMessages>}
 */

const hasCachedMessagesExpired = (sessionId) => {
  const refreshAt = cache.getSessionStorage(
    messagesRefreshAtCacheKey(sessionId),
  );
  return !!refreshAt && Date.now() >= Number(refreshAt);
};

/**
 * Stores the soonest time at which the cached messages should be treated as
 * stale, based on each message config's refreshAfterSeconds, so a pending
 * status (e.g. email-verification) gets re-fetched on a later navigation
 * once it may have changed.
 * @param {string} sessionId
 * @param {GWSMessages} messagesData
 */
const updateMessagesRefreshAt = (sessionId, messagesData) => {
  const refreshDelaysSeconds = messagesData.messages
    .map((message) =>
      getConfig(message.messageId)?.refreshAfterSeconds?.(
        message.additionalAttributes,
      ),
    )
    .filter((delaySeconds) => typeof delaySeconds === 'number');

  if (refreshDelaysSeconds.length === 0) {
    cache.removeSessionStorage(messagesRefreshAtCacheKey(sessionId));
    return;
  }

  const soonestDelaySeconds = Math.min(...refreshDelaysSeconds);
  cache.setSessionStorage(
    messagesRefreshAtCacheKey(sessionId),
    String(Date.now() + soonestDelaySeconds * 1000),
  );
};

const getMessageData = async (sessionId, serviceUrl) => {
  let messagesData = cache.getSessionStorage(messageIdCacheKey(sessionId));

  if (messagesData && !hasCachedMessagesExpired(sessionId)) {
    messagesData = await JSON.parse(messagesData);
  } else {
    messagesData = await http.getJSON(serviceUrl, {
      'X-Transaction-ID': uuidv4(),
      'X-Request-ID': uuidv4(),
    });

    if (sessionId) {
      cache.setSessionStorage(
        messageIdCacheKey(sessionId),
        JSON.stringify(messagesData),
      );
      updateMessagesRefreshAt(sessionId, messagesData);
    }
  }
  return messagesData;
};

/**
 * @typedef {Object} messageDetails
 * @property {string} bannerId          Same value as BannerTag in message definition
 * @property {string} descriptionId     Same value as DescriptionTag in message definition
 * @property {('warning'|'error')} severity
 * @property {Object} [additionalAttributes]
 */

/**
 * @typedef {Object} mappedMessage
 * @property {('project'|'account')} type
 * @property {messageDetails} message
 */

/**
 *
 * @param {message} messageData
 * @returns {{type: 'project'|'account', message: mappedMessage}}
 */

const castIdToMessage = (messageData) => {
  const {
    bannerTag,
    descriptionTag,
    type,
    severity,
    additionalAttributes,
    descriptionComponent,
    shouldDisplay,
  } = getConfig(messageData.messageId);

  if (shouldDisplay && !shouldDisplay(messageData.additionalAttributes)) {
    return null;
  }

  const translatedAttributes = additionalAttributes.map((attribute) => {
    if (attribute.requireTranslate) {
      return messageService.get(
        messageData.additionalAttributes[attribute.name],
      );
    }
    return messageData.additionalAttributes[attribute.name];
  });

  return {
    type,
    message: {
      bannerId: bannerTag,
      descriptionId: descriptionTag || messageData.messageId,
      descriptionComponent,
      severity: severity || 'warning',
      additionalAttributes: translatedAttributes,
    },
  };
};

/**
 * @typedef {Object} SplittedMessages
 * @property {messageDetails} account
 * @property {messageDetails} project
 */

/**
 * It splits messages to types
 * @param {GWSMessages} messagesData
 * @returns {SplittedMessages}
 */
const getMessagesToDisplay = (messagesData) => {
  return messagesData.messages.reduce(
    (messagesToDisplay, message) => {
      const result = castIdToMessage(message);
      if (!result) {
        return messagesToDisplay;
      }
      messagesToDisplay[result.type] = [
        ...messagesToDisplay[result.type],
        result.message,
      ];
      return messagesToDisplay;
    },
    { account: [], project: [] },
  );
};
